import type { ConfigurationResponse } from '../types';
import { getDebugInfo } from './getDebugInfo';

export type AttributeValue = string | number | boolean;

/**
 * Sentry attributes must be flat scalars — there is no nested-attribute support — and an
 * absent field is better dropped than sent as null, which would show up as its own value
 * to group by.
 */
const compact = (
  attributes: Record<string, AttributeValue | null | undefined>,
): Record<string, AttributeValue> =>
  Object.entries(attributes).reduce<Record<string, AttributeValue>>(
    (result, [key, value]) =>
      value === null || value === undefined ? result : { ...result, [key]: value },
    {},
  );

/**
 * Builds the shared context every metric carries, as an explicit key -> source map so a
 * metric never silently gains or loses a dimension when the debug payload changes.
 *
 * Deliberately narrow: a dimension is only worth carrying if you would group or filter a
 * chart by it. Excluded for that reason are anything effectively unique per user or per
 * build (commit sha, feature flags, locale, the route — which is always the same screen
 * for a given metric) and the raw user agent, ewsUrl and restUrl, which would also
 * dominate the 2KB per-metric budget. The mailbox address is excluded as well: Sentry
 * already attaches `user.email` to every metric from the scope, so mapping it here would
 * only duplicate it.
 *
 * Configuration is passed in rather than read from its store: this module sits under
 * `@utils-global`, which `@data` already imports, so reaching back into the store would
 * close an import cycle. Callers running before the configuration query resolves pass
 * nothing, and the organisation id is omitted.
 */
export const getMetricAttributes = (
  config?: ConfigurationResponse['data'],
): Record<string, AttributeValue> => {
  const debugInfo = getDebugInfo({ config });

  return compact({
    'app.manifestVersion': debugInfo.app.manifestVersion,
    'app.environment': debugInfo.app.environment,
    'app.release': debugInfo.app.release,
    'outlook.hostName': debugInfo.outlook.hostName,
    'outlook.hostVersion': debugInfo.outlook.hostVersion,
    'outlook.platform': debugInfo.outlook.platform,
    'outlook.officeHost': debugInfo.outlook.officeHost,
    'outlook.officeVersion': debugInfo.outlook.officeVersion,
    'outlook.accountType': debugInfo.outlook.accountType,
    'outlook.onPremises': debugInfo.outlook.onPremises,
    'config.organisation-id': debugInfo.config?.['organisation-id'],
  });
};
